import { getRenderableEffects } from './effectModel';

const toArray = (value) => (Array.isArray(value) ? value : []);

// 2026-05-11: Legacy text-keyed inventory scan. Kept as a backward-
// compat fallback for older buff data that doesn't have per-instance
// provenance stamped on it (pre-occurrence-key writes). NOT used when
// the effect carries explicit item provenance — that path uses the
// per-instance metadata directly to avoid the duplicate-source bug
// where two equipped items giving the same buff would have their
// attribution swap based on inventory iteration order.
const findLinkedEquippedItemName = (inventory = [], effectText = '', occurrence = 0) => {
  let matchIndex = 0;
  for (const item of toArray(inventory)) {
    if (!item?.equipped) continue;
    const itemEffectCount =
      toArray(item.buffs).filter((entry) => entry === effectText).length
      + toArray(item.debuffs).filter((entry) => entry === effectText).length;
    for (let index = 0; index < itemEffectCount; index += 1) {
      if (matchIndex === occurrence) return item.name;
      matchIndex += 1;
    }
  }
  return null;
};

// 2026-05-11: Resolve linkedItemName via per-instance provenance first,
// then verify the linked item is actually equipped, then fall back to
// the legacy text scan only for buffs without explicit metadata.
//
// Bug fixed: with two equipped items giving the same buff text (e.g.,
// Phantom Greatsword + Flamebrand Sword both giving "+1 Str"), the
// previous text-scan returned whichever equipped item happened to come
// first in inventory order — disagreeing with the buffData provenance
// when inventory order didn't match occurrence order. Trust provenance
// over text scans.
//
// Also: when a buff's provenance points to an item that's no longer
// equipped (stale state), return null instead of lying that the unequipped
// item is the source. And: don't text-scan for buffs with explicit
// non-item sourceMode (manual / DM / spell-cast / diet / etc.) — those
// would falsely attribute to a coincidentally-named equipped item.
const getLinkedItemName = (effect, inventory) => {
  // PRIORITY 1: Item-stamped provenance. Find the matching equipped
  // item by its id (most precise — survives renames, duplicates, and
  // inventory reordering).
  if (effect.sourceType === 'item') {
    const mode = String(effect.sourceMode || '').toLowerCase();
    // item-ability and item-consume don't render with a "linked equipped
    // item" badge — those effects come FROM an item action but aren't
    // tied to its equipped state.
    if (mode === 'item-ability' || mode === 'item-consume') return null;
    const itemId = effect.itemEntryId || effect.itemId;
    if (itemId && Array.isArray(inventory)) {
      const equippedMatch = inventory.find((i) => i?.equipped && i?.id === itemId);
      if (equippedMatch) return equippedMatch.name || effect.itemName || null;
      // Provenance points to an item not currently equipped — don't lie.
      return null;
    }
    // No id but a name — fall back to verifying by name (older data
    // without itemEntryId stamped).
    if (effect.itemName && Array.isArray(inventory)) {
      const byName = inventory.find((i) => i?.equipped && i?.name === effect.itemName);
      return byName ? byName.name : null;
    }
    return null;
  }

  // PRIORITY 2: Effects with explicit non-item sourceMode (manual, DM,
  // spell-cast, diet, system, etc.) — never text-scan inventory. Those
  // buffs aren't from items; finding a matching equipped item by text
  // would be a false attribution.
  if (effect.sourceMode) return null;

  // PRIORITY 3: Truly legacy data (no sourceMode, no provenance) —
  // fall back to the original text-keyed inventory scan. Backward
  // compat for buffs written before per-instance metadata existed.
  return findLinkedEquippedItemName(inventory, effect.text || effect.name, effect.occurrence || 0);
};

const isAutomaticSource = (effect = {}) => {
  if (effect.sourceType === 'item') return true;
  const mode = String(effect.sourceMode || '').toLowerCase();
  return mode.startsWith('item-')
    || mode === 'equip'
    || mode === 'consume'
    || mode === 'diet'
    || mode === 'spell'
    || mode === 'disease';
};

const getAutomaticItemSourceLabel = (effect = {}) => {
  if (effect.sourceType !== 'item') return null;
  const mode = String(effect.sourceMode || '').toLowerCase();
  if (mode === 'equip' || mode === 'item-equip') return null;
  return effect.source || effect.itemName || effect.provenance?.itemName || null;
};

const toPresentationEntry = (effect, inventory, glowingBuffsForChar) => ({
  effectId: effect.effectId,
  effectInstanceId: effect.effectInstanceId || null,
  effectGroupId: effect.effectGroupId,
  legacyKey: effect.legacyKey,
  occurrence: effect.occurrence,
  listKey: effect.listKey,
  listIndex: effect.listIndex,
  category: effect.category,
  permanence: effect.permanence,
  isPermanent: effect.permanence === 'permanent',
  isDisabled: effect.isDisabled === true,
  isHidden: effect.isHidden === true,
  text: effect.text || effect.name,
  label: effect.name || effect.text,
  source: effect.source || null,
  note: effect.note || null,
  sourceType: effect.sourceType || null,
  storageSourceType: effect.storageSourceType || null,
  provenance: effect.provenance || null,
  itemEntryId: effect.itemEntryId || null,
  itemId: effect.itemId || null,
  itemDefinitionId: effect.itemDefinitionId || null,
  itemName: effect.itemName || null,
  abilityId: effect.abilityId || null,
  abilityName: effect.abilityName || null,
  sourceMode: effect.sourceMode || null,
  sourceLabel: getAutomaticItemSourceLabel(effect),
  playerTooltip: effect.playerTooltip || '',
  colorId: effect.colorId || null,
  noteVisible: effect.noteVisible === true && !isAutomaticSource(effect),
  linkedItemName: getLinkedItemName(effect, inventory),
  glowColor: effect.legacyKey ? glowingBuffsForChar?.[effect.legacyKey]?.color || null : null,
});

export const buildLegacyStatusEffectView = ({ char = null, glowingBuffsForChar = {} } = {}) => {
  if (!char) {
    return {
      allEffects: [],
      activeEffects: [],
      visibleBuffs: [],
      visibleDebuffs: [],
      hiddenBuffs: [],
      hiddenDebuffs: [],
      disabledBuffs: [],
      disabledDebuffs: [],
    };
  }

  const renderableEffects = getRenderableEffects({
    char,
    includeLegacy: true,
    includeRuntime: false,
    includeConditions: false,
    includeConcentration: false,
    includeMarks: false,
  })
    .filter((effect) => effect.storageSourceType === 'legacy' || effect.sourceType === 'legacy' || effect.sourceType === 'item')
    .map((effect) => toPresentationEntry(effect, char.inventory, glowingBuffsForChar));

  const isVisible = (effect) => !effect.isDisabled && !effect.isHidden;
  const isHidden = (effect) => !effect.isDisabled && effect.isHidden;
  const isDisabled = (effect) => effect.isDisabled;
  const isBuff = (effect) => effect.category === 'buff';
  const isDebuff = (effect) => effect.category === 'debuff';

  return {
    allEffects: renderableEffects,
    activeEffects: renderableEffects.filter((effect) => !effect.isDisabled),
    visibleBuffs: renderableEffects.filter((effect) => isBuff(effect) && isVisible(effect)),
    visibleDebuffs: renderableEffects.filter((effect) => isDebuff(effect) && isVisible(effect)),
    hiddenBuffs: renderableEffects.filter((effect) => isBuff(effect) && isHidden(effect)),
    hiddenDebuffs: renderableEffects.filter((effect) => isDebuff(effect) && isHidden(effect)),
    disabledBuffs: renderableEffects.filter((effect) => isBuff(effect) && isDisabled(effect)),
    disabledDebuffs: renderableEffects.filter((effect) => isDebuff(effect) && isDisabled(effect)),
  };
};
